<template>
  <section class="article-registration-cta" aria-labelledby="article-cta-heading">
    <div class="article-cta-inner">
      <h2 id="article-cta-heading" class="article-cta-heading">
        {{ heading }}
      </h2>
      <div class="article-cta-offer">
        <p class="article-cta-text">{{ offerText }}</p>
        <div class="article-cta-button-wrap">
          <UIButton
            :href="registrationHref"
            variant="primary-arrow"
            arrow-direction="right"
            :text="buttonText"
            padding="14px 28px"
            rounded="8px"
            size="large"
            class="article-cta-button"
            @click.prevent="openRegistrationDialog"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
interface Props {
  heading?: string
  offerText?: string
  buttonText?: string
  registrationHref?: string
}

const { t } = useI18n()
const props = withDefaults(defineProps<Props>(), {
  heading: undefined,
  offerText: undefined,
  buttonText: undefined,
  registrationHref: undefined,
})

const heading = computed(() => props.heading ?? t('articleDetails.cta.heading'))
const offerText = computed(() => props.offerText ?? t('articleDetails.cta.offerText'))
const buttonText = computed(() => props.buttonText ?? t('articleDetails.cta.button'))

const registrationHref = computed(() => {
  if (props.registrationHref) return props.registrationHref
  const config = useRuntimeConfig()
  const base = (config.public as { NUXT_APP_STUDENT_UI?: string }).NUXT_APP_STUDENT_UI || 'https://study.bibleawr.com'
  const url = base.replace(/\/$/, '')
  return `${url}/signup`
})

const { open: openRegistrationDialog } = useRegistrationFormDialog()
</script>

<style scoped>
.article-registration-cta {
  margin-top: 40px;
  padding: 40px;
  background: var(--color-bg-cta);
  border-radius: 12px;
  font-family: var(--font-inter);
}

.article-cta-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 40px;
  max-width: 100%;
}

.article-cta-heading {
  font-family: var(--font-inter);
  font-weight: var(--font-weight-bold);
  font-size: 32px;
  line-height: var(--line-height-heading);
  color: var(--color-dark-blue);
  margin: 0;
}

.article-cta-offer {
  display: grid;
  align-items: center;
  gap: 24px;
}

.article-cta-text {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  color: var(--color-section-title);
  margin: 0;
  text-align: left;
}

.article-cta-button-wrap {
  flex-shrink: 0;
}

.article-cta-button {
  font-family: var(--font-inter);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

@media (max-width: 991px) {
  .article-cta-inner {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .article-cta-heading {
    font-size: 20px;
    text-align: center;
  }

  .article-cta-offer {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }

  .article-cta-text {
    text-align: center;
  }
}

@media (max-width: 767px) {
  .article-registration-cta {
    padding: 24px 20px;
    margin-top: 32px;
  }

  .article-cta-heading {
    font-size: 18px;
  }

  .article-cta-text {
    font-size: var(--font-size-small);
  }
}
</style>
